import { useState, useCallback } from 'react';
import { useAccounts } from './useAccounts';
import { useTransactions } from './useTransactions';
import { useCategoryBudgets } from './useCategoryBudgets';
import { useTranslation } from './useTranslation';
import { useCurrency } from '@/contexts';
import { supabase } from '@/lib/supabase';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface UseAiChatReturn {
  sendMessage: (message: string, history?: ChatMessage[]) => Promise<string>;
  isLoading: boolean;
  error: string | null;
  isConfigured: boolean;
}

export function useAiChat(): UseAiChatReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accounts } = useAccounts();
  const { transactions, calculateStats } = useTransactions();
  const { budgets: budgetsWithProgress, budgetStats } = useCategoryBudgets();
  const { language } = useTranslation();
  const { currency } = useCurrency();

  // Edge Function is always "configured" - the server handles the API key
  const isConfigured = true;

  // Build context from user's financial data
  const buildFinancialContext = useCallback(() => {
    const stats = calculateStats();
    const currencyCode = currency;

    // Get recent transactions (last 30)
    const recentTransactions = transactions.slice(0, 30).map((t) => ({
      date: t.date,
      amount: t.amount,
      type: t.type,
      description: t.description,
      category: t.category?.name || (language === 'fr' ? 'Non catégorisé' : 'Uncategorized'),
    }));

    // Get account summaries
    const accountSummaries = accounts.map((a) => ({
      name: a.name,
      type: a.type,
      balance: a.balance,
      currency: a.currency,
    }));

    // Calculate category spending
    const categorySpending: Record<string, number> = {};
    transactions
      .filter((t) => t.type === 'expense')
      .forEach((t) => {
        const cat = t.category?.name || (language === 'fr' ? 'Autre' : 'Other');
        categorySpending[cat] = (categorySpending[cat] || 0) + Math.abs(t.amount);
      });

    const totalBalance = accounts.reduce((sum, a) => sum + a.balance, 0);

    if (language === 'fr') {
      return `
DONNÉES FINANCIÈRES DE L'UTILISATEUR :

📊 Résumé du mois :
- Revenus totaux : ${stats.totalIncome.toFixed(2)} ${currencyCode}
- Dépenses totales : ${stats.totalExpense.toFixed(2)} ${currencyCode}
- Balance nette : ${stats.balance.toFixed(2)} ${currencyCode}

💰 Comptes (${accounts.length}) :
${accountSummaries.map((a) => `- ${a.name} (${a.type}): ${a.balance.toFixed(2)} ${a.currency}`).join('\n')}
- TOTAL : ${totalBalance.toFixed(2)} ${currencyCode}

📈 Dépenses par catégorie :
${Object.entries(categorySpending)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 8)
  .map(([cat, amount]) => `- ${cat}: ${amount.toFixed(2)} ${currencyCode}`)
  .join('\n')}

📝 Transactions récentes (dernières ${recentTransactions.length}) :
${recentTransactions
  .slice(0, 10)
  .map(
    (t) =>
      `- ${t.date}: ${t.type === 'expense' ? '-' : '+'}${Math.abs(t.amount).toFixed(2)} ${currencyCode} - ${t.description || t.category}`
  )
  .join('\n')}

🎯 Budgets par catégorie (${budgetsWithProgress.length}) :
${
  budgetsWithProgress.length > 0
    ? budgetsWithProgress
        .map(
          (b) =>
            `- ${b.category?.name || 'Sans catégorie'}: ${b.spent.toFixed(2)}/${b.amount_limit.toFixed(2)} ${currencyCode} (${b.percentage.toFixed(0)}%) - ${b.isOverBudget ? '⚠️ DÉPASSÉ' : b.percentage >= 80 ? '⚠️ Proche limite' : '✅ OK'}`
        )
        .join('\n')
    : 'Aucun budget défini'
}
${
  budgetsWithProgress.length > 0
    ? `
Résumé budgets :
- Budget total : ${budgetStats.totalBudget.toFixed(2)} ${currencyCode}
- Dépensé : ${budgetStats.totalSpent.toFixed(2)} ${currencyCode}
- Restant : ${budgetStats.totalRemaining.toFixed(2)} ${currencyCode}
- Budgets dépassés : ${budgetStats.overBudgetCount}
- Budgets proches limite : ${budgetStats.nearLimitCount}`
    : ''
}

Devise utilisateur : ${currencyCode}
`;
    }

    return `
USER FINANCIAL DATA:

📊 Monthly Summary:
- Total Income: ${stats.totalIncome.toFixed(2)} ${currencyCode}
- Total Expenses: ${stats.totalExpense.toFixed(2)} ${currencyCode}
- Net Balance: ${stats.balance.toFixed(2)} ${currencyCode}

💰 Accounts (${accounts.length}):
${accountSummaries.map((a) => `- ${a.name} (${a.type}): ${a.balance.toFixed(2)} ${a.currency}`).join('\n')}
- TOTAL: ${totalBalance.toFixed(2)} ${currencyCode}

📈 Spending by Category:
${Object.entries(categorySpending)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 8)
  .map(([cat, amount]) => `- ${cat}: ${amount.toFixed(2)} ${currencyCode}`)
  .join('\n')}

📝 Recent Transactions (last ${recentTransactions.length}):
${recentTransactions
  .slice(0, 10)
  .map(
    (t) =>
      `- ${t.date}: ${t.type === 'expense' ? '-' : '+'}${Math.abs(t.amount).toFixed(2)} ${currencyCode} - ${t.description || t.category}`
  )
  .join('\n')}

🎯 Category Budgets (${budgetsWithProgress.length}):
${
  budgetsWithProgress.length > 0
    ? budgetsWithProgress
        .map(
          (b) =>
            `- ${b.category?.name || 'Uncategorized'}: ${b.spent.toFixed(2)}/${b.amount_limit.toFixed(2)} ${currencyCode} (${b.percentage.toFixed(0)}%) - ${b.isOverBudget ? '⚠️ EXCEEDED' : b.percentage >= 80 ? '⚠️ Near limit' : '✅ OK'}`
        )
        .join('\n')
    : 'No budgets set'
}
${
  budgetsWithProgress.length > 0
    ? `
Budget Summary:
- Total Budget: ${budgetStats.totalBudget.toFixed(2)} ${currencyCode}
- Total Spent: ${budgetStats.totalSpent.toFixed(2)} ${currencyCode}
- Remaining: ${budgetStats.totalRemaining.toFixed(2)} ${currencyCode}
- Exceeded Budgets: ${budgetStats.overBudgetCount}
- Near Limit Budgets: ${budgetStats.nearLimitCount}`
    : ''
}

User Currency: ${currencyCode}
`;
  }, [
    accounts,
    transactions,
    calculateStats,
    budgetsWithProgress,
    budgetStats,
    language,
    currency,
  ]);

  const sendMessage = useCallback(
    async (userMessage: string, history?: ChatMessage[]): Promise<string> => {
      setIsLoading(true);
      setError(null);

      try {
        const financialContext = buildFinancialContext();

        const { data, error: fnError } = await supabase.functions.invoke('ai-chat', {
          body: {
            message: userMessage,
            history,
            financialContext,
            language,
          },
        });

        if (fnError) {
          throw new Error(fnError.message);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        return (
          data?.response ||
          (language === 'fr'
            ? "Je n'ai pas pu générer de réponse."
            : 'I could not generate a response.')
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : language === 'fr'
              ? 'Erreur inconnue'
              : 'Unknown error';
        setError(errorMessage);
        return language === 'fr'
          ? `Désolé, une erreur s'est produite: ${errorMessage}`
          : `Sorry, an error occurred: ${errorMessage}`;
      } finally {
        setIsLoading(false);
      }
    },
    [buildFinancialContext, language]
  );

  return {
    sendMessage,
    isLoading,
    error,
    isConfigured,
  };
}
